import {
  IonFooter,
  IonHeader,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  IonTitle,
  type RefresherEventDetail,
} from "@ionic/react";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { BackButton } from "@/components/back-button";
import { Button } from "@/components/button";
import { Buttons } from "@/components/buttons";
import { Content } from "@/components/content";
import { ModalSheetAutoHeight } from "@/components/modal/sheet/auto-height";
import { EditableAvatar } from "@/components/molecules/auth/editable-avatar";
import { ProfileTeamsSection } from "@/components/molecules/employee/profile-teams-section";
import { Toolbar } from "@/components/toolbar";
import { useBoomerForm } from "@/form/boomer/hook";
import { CustomFieldFile } from "@/form/components/custom-field-file";
import { CustomFieldInput } from "@/form/components/custom-field-input";
import { CustomFieldMultiSelect } from "@/form/components/custom-field-multi-select";
import { CustomFieldSelect } from "@/form/components/custom-field-select";
import { CustomFieldToggle } from "@/form/components/custom-field-toggle";
import { api } from "@/lib/backend";
import {
  type CustomField,
  type CustomFieldDefinition,
  type CustomFieldValueInput,
  type UpdateMeInput,
} from "@/lib/backend/api";
import {
  hasDayOfBirthChanged,
  submitVerifiedDayOfBirth,
  toDateInputValue,
} from "@/lib/backend/day-of-birth-verify";
import { getMissingProfileFields } from "@/lib/backend/profile-completeness";
import { extractionWrapper } from "@/lib/backend/utils/extract";
import { useExtractedMutation } from "@/lib/backend/utils/hooks";
import {
  filterEmployeeVisibleCustomFields,
  isEmployeeEditableCustomField,
} from "@/lib/custom-fields/employee-visible";
import { useMe } from "@/lib/hooks/use-me";
import { ME_QUERY_KEY } from "@/lib/providers/query/functions/user/me.key";
import { Routes } from "@/lib/router";
import { logger } from "@/logger";

const TOAST_KEY = "update-personal-info";

/**
 * Een invoerveld binnen een blok heeft zelf geen kader.
 *
 * Het blok is het kader -- één afgeronde kaart met haarlijnen ertussen, zoals de instellingen van
 * het toestel zelf. Een omlijnd veld bínnen die kaart is een tweede rand om hetzelfde ding, en dat
 * is precies waar het scherm zwaar van werd: negen dozen onder elkaar.
 *
 * `bg-transparent` in plaats van het `bg-white` van de component is meteen de reparatie van een
 * stille fout: dat wit stond er hard in, en op een donker scherm was dat een wit vlak.
 */
const FIELD_IN_ROW = "border-0 bg-transparent px-0 py-0 focus:ring-0";

/** Eén regel in een blok. */
function Row({ children }: { children: React.ReactNode }) {
  return <div className="px-4 py-3">{children}</div>;
}

/**
 * Een blok met een kop, in de vorm die iOS voor instellingen gebruikt: een kleine kop erboven en
 * een afgeronde kaart eronder waarin de regels door haarlijnen gescheiden worden.
 *
 * `missing` zet er een merkteken bij. Dat is de brug naar de instelkaart op Vandaag: die zegt "je
 * adres ontbreekt nog" en stuurt je hierheen, en tot nu toe stond je hier dan voor negen identieke
 * velden zonder dat er één was aangewezen.
 */
function ProfileSection({
  id,
  title,
  missing = false,
  missingLabel,
  children,
}: {
  id: string;
  title: string;
  missing?: boolean;
  missingLabel: string;
  children: React.ReactNode;
}) {
  return (
    <section id={`profile-section-${id}`} className="flex scroll-mt-4 flex-col gap-2">
      <div className="flex items-center gap-2 px-1">
        <h2 className="text-13 font-semibold uppercase tracking-wide text-txt-tertiary">{title}</h2>

        {missing && (
          <span className="rounded-full bg-warning-10 px-2 py-0.5 text-xsm font-semibold text-warning-120">
            {missingLabel}
          </span>
        )}
      </div>

      <div
        className={`flex flex-col divide-y divide-solid divide-bordercolor-primary overflow-hidden rounded-2xl border border-solid bg-surface-primary ${
          missing ? "border-warning-100" : "border-bordercolor-primary"
        }`}
      >
        {children}
      </div>
    </section>
  );
}

type PersonalInfoFormValue = {
  firstname: string;
  lastname: string;
  city: string;
  email: string;
  phone: string;
  addressLine: string;
  state: string;
  zipCode: string;
  dayOfBirth: string;
  [key: string]: string | number | boolean | string[] | File | null;
};

function getDefaultValueFromCustomField(field: CustomField): any {
  const { value, customFieldDefinition } = field;

  if (!value) {
    return null;
  }

  switch (customFieldDefinition.type) {
    case "TEXT":
      return value.text ?? "";
    case "NUMBER":
      return value.number ?? null;
    case "DATE":
      return value.date ?? "";
    case "SELECT":
      return value.select ?? "";
    case "MULTI_SELECT":
      return value.multiSelect ?? [];
    case "BOOLEAN":
      return value.boolean ?? false;
    case "FILE":
      // Return the full value object so CustomFieldFile can detect existing files
      // and fetch signed URLs for display
      return value;
    default:
      return null;
  }
}

function buildCustomFieldValueInput(
  type: CustomFieldDefinition["type"],
  value: any,
): CustomFieldValueInput {
  const input: CustomFieldValueInput = {
    text: null,
    number: null,
    date: null,
    select: null,
    multiSelect: null,
    boolean: null,
  };

  switch (type) {
    case "TEXT":
      input.text = value || null;
      break;
    case "NUMBER":
      // Handle number: null or undefined becomes null, otherwise convert to number
      // This properly handles 0 as a valid value
      input.number = value === null || value === undefined || value === "" ? null : Number(value);
      break;
    case "DATE":
      input.date = value || null;
      break;
    case "SELECT":
      input.select = value || null;
      break;
    case "MULTI_SELECT":
      input.multiSelect = Array.isArray(value) ? (value as string[]) : [];
      break;
    case "BOOLEAN":
      input.boolean = Boolean(value);
      break;
  }

  return input;
}

export function SettingsPersonalInformationPage() {
  const { t } = useTranslation();
  const { me } = useMe();
  const queryClient = useQueryClient();

  const employeeId = me?.employee?.id;

  /**
   * Wat de server mist aan dit profiel: `photo`, `dayOfBirth`, `address` (de drie adresvelden als
   * één geheel) en `customField:<id>`. Zie `api/src/modules/me/profile-completeness.ts`.
   *
   * Let op `photo`: die stond niet op dit scherm. De instelkaart op Vandaag kon je dus hierheen
   * sturen met "je foto ontbreekt nog" en dan was er niets te doen. Vandaar dat de foto nu bovenaan
   * staat en niet alleen op Instellingen.
   */
  const missingFields = getMissingProfileFields(me);
  const missesPhoto = missingFields.includes("photo");
  const missesDayOfBirth = missingFields.includes("dayOfBirth");
  const missesAddress = missingFields.includes("address");
  const missesCustomField = missingFields.some((key) => key.startsWith("customField:"));
  const missingLabel = t("routes:personal-information.missing");

  // Fetch custom fields
  const {
    data: rawCustomFields,
    isLoading: isLoadingCustomFields,
    refetch: refetchCustomFields,
  } = useQuery({
    queryKey: ["custom-fields", "EMPLOYEE", employeeId],
    queryFn: () => extractionWrapper(api.customFields.getAll("EMPLOYEE", employeeId!)),
    enabled: !!employeeId,
  });

  // Defense-in-depth: never render or patch admin-only fields in the employee
  // app. The api already excludes ADMIN_ONLY definitions for employees and
  // rejects writes to them, but we filter here too so they can't leak via a
  // stale/mismatched client. See #463.
  const customFields = filterEmployeeVisibleCustomFields(rawCustomFields);

  /**
   * Verplicht, maar pas als je het veld verlaat.
   *
   * Dit hing aan `onChange`: bij de eerste letter die je typte stond het veld al rood, en wie een
   * veld leegmaakte om het opnieuw in te vullen kreeg de fout te zien terwijl hij bezig was. Op
   * `onBlur` zegt het hetzelfde op het moment dat je klaar bent, en `onSubmit` houdt de poort dicht
   * voor een veld dat je nooit hebt aangeraakt -- anders zou "niet meer zeuren" stilletjes
   * betekenen dat een leeg verplicht veld erdoor glipt.
   */
  const requiredField = (messageKey: string) => {
    const check = ({ value }: { value: unknown }) =>
      typeof value === "string" && value.trim() !== "" ? undefined : t(messageKey);

    return { onBlur: check, onSubmit: check };
  };

  /**
   * Kom je hier omdat er iets ontbreekt, dan begint het scherm daar.
   *
   * Eén keer per bezoek, en alleen als de server ook echt iets mist. De velden van de organisatie
   * laden apart, dus er wordt gewacht tot die er zijn -- anders springt hij naar een blok dat een
   * tel later alsnog verschuift.
   */
  const jumpedToMissing = useRef(false);
  const firstMissingSection = missesPhoto
    ? "photo"
    : missesDayOfBirth
      ? "name"
      : missesAddress
        ? "address"
        : missesCustomField
          ? "custom"
          : null;

  useEffect(() => {
    if (!firstMissingSection || jumpedToMissing.current || isLoadingCustomFields) {
      return;
    }

    const target = document.getElementById(`profile-section-${firstMissingSection}`);

    if (!target) {
      return;
    }

    jumpedToMissing.current = true;
    // `scroll-padding-top` op de scroller houdt hem onder het glas vandaan; zie
    // styles/ionic/overrides/content.css.
    target.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [firstMissingSection, isLoadingCustomFields]);

  const handleRefresh = async (event: CustomEvent<RefresherEventDetail>) => {
    await refetchCustomFields();
    event.detail.complete();
  };

  // Update personal info mutation
  const updateMeMutation = useExtractedMutation({
    mutationKey: ["me", "update"],
    mutationFn: (input: UpdateMeInput) => api.me.updateMe(input),
    onMutate: () => {
      // Sonner's toast.loading() leaves a white overlay stuck on iOS (see
      // docs/common-issues.md). The submit button already shows a loading state
      // via form.state.isSubmitting, so just clear any stale toast here.
      toast.dismiss(TOAST_KEY);
    },
    // Bewust geen toast hier. Deze mutatie is de eerste van drie stappen -- de eigen velden, dan
    // de velden van de organisatie, dan eventueel de geboortedatum -- en wie halverwege "gelukt"
    // roept, roept dat ook als de stap erna faalt. Dat gebeurde: bij een custom field met een fout
    // verscheen eerst "opgeslagen" en daarna "opslaan mislukt". De uitkomst hoort bij het einde,
    // en dat is `onSubmit`.
    onError: (error) => {
      logger.error(error);
    },
  });

  // Update custom field mutation
  const updateCustomFieldMutation = useExtractedMutation({
    mutationFn: async ({
      definitionId,
      value,
      type,
    }: {
      definitionId: string;
      value: any;
      type: CustomFieldDefinition["type"];
    }) => {
      if (type === "FILE" && value instanceof File) {
        logger.info("Uploading file", { definitionId, fileName: value.name, fileSize: value.size });
        return api.customFields.uploadFile("EMPLOYEE", employeeId!, definitionId, { file: value });
      }
      const input = buildCustomFieldValueInput(type, value);
      return api.customFields.update("EMPLOYEE", employeeId!, definitionId, input);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["custom-fields", "EMPLOYEE", employeeId],
      });
    },
  });

  // Build default values including custom fields
  const defaultValues: PersonalInfoFormValue = {
    firstname: me?.firstname ?? "",
    lastname: me?.lastname ?? "",
    city: me?.employee?.city ?? "",
    email: me?.email ?? "",
    phone: me?.employee?.phone ?? "",
    addressLine: me?.employee?.addressLine ?? "",
    state: me?.employee?.state ?? "",
    zipCode: me?.employee?.zipCode ?? "",
    dayOfBirth: toDateInputValue(me?.employee?.dayOfBirth) ?? "",
  };

  // Day-of-birth verification flow (#383): geboortedatum drives loonschalen, so
  // a change is saved via a dedicated verified endpoint behind an explicit
  // confirmation step rather than silently overwritten.
  const dobConfirmRef = useRef<HTMLIonModalElement>(null);
  const pendingDobRef = useRef<string | null>(null);

  const saveVerifiedDayOfBirth = async () => {
    const value = pendingDobRef.current;
    if (!value) {
      return;
    }
    try {
      await submitVerifiedDayOfBirth(value);
      await queryClient.invalidateQueries({ queryKey: [ME_QUERY_KEY] });
      toast.success(t("actions:update-me.success"), { id: TOAST_KEY });
    } catch (error) {
      toast.error(t("actions:update-me.error"), { id: TOAST_KEY });
      logger.error("Failed to update day of birth", error);
    } finally {
      pendingDobRef.current = null;
    }
  };

  // Add custom fields to default values
  customFields?.forEach((field) => {
    defaultValues[field.customFieldDefinition.id] = getDefaultValueFromCustomField(field);
  });

  const [form, { Element: FormElement }] = useBoomerForm({
    defaultValues,
    onSubmit: async ({ value }) => {
      // Sonner's toast.loading() leaves a white overlay stuck on iOS (see
      // docs/common-issues.md). The submit button already shows a loading state
      // via form.state.isSubmitting, so just clear any stale toast here.
      toast.dismiss(TOAST_KEY);

      try {
        // Update personal info
        await updateMeMutation.mutateAsync({
          firstname: value.firstname,
          lastname: value.lastname,
          city: value.city,
          email: value.email,
          phone: value.phone,
          addressLine: value.addressLine,
          state: value.state,
          zipCode: value.zipCode,
        });

        // Update custom fields one by one to catch individual field errors
        let hasFieldErrors = false;
        const fieldErrorMessages: string[] = [];

        for (const field of customFields || []) {
          const definitionId = field.customFieldDefinition.id;
          const fieldValue = value[definitionId];

          // #10a: never submit read-only fields. The api rejects employee writes
          // to them (server-enforced), so skip to avoid a guaranteed 403.
          if (!isEmployeeEditableCustomField(field)) {
            continue;
          }

          // Skip FILE fields that don't have a new File object selected
          if (field.customFieldDefinition.type === "FILE" && !(fieldValue instanceof File)) {
            continue;
          }

          try {
            await updateCustomFieldMutation.mutateAsync({
              definitionId,
              value: fieldValue,
              type: field.customFieldDefinition.type,
            });
          } catch (fieldError: any) {
            hasFieldErrors = true;

            // Set error on the specific field
            const rawMessage = Array.isArray(fieldError?.message)
              ? fieldError.message.join(" ")
              : fieldError?.message || t("fields:custom.generic-error");

            // Translate common backend validation errors
            let errorMessage = rawMessage;
            if (rawMessage === "Select value is required") {
              errorMessage = t("fields:custom.select-required");
            } else if (rawMessage === "Text value is required") {
              errorMessage = t("common:validation.required");
            } else if (rawMessage === "Number value is required") {
              errorMessage = t("common:validation.required");
            } else if (rawMessage === "Date value is required") {
              errorMessage = t("fields:custom.date-required");
            } else if (rawMessage === "File is required") {
              errorMessage = t("fields:custom.file-required");
            }

            // Get the field and set error
            const fieldInstance = form.getFieldInfo(definitionId);
            if (fieldInstance) {
              const currentMeta = fieldInstance.instance?.state.meta;
              form.setFieldMeta(definitionId, {
                isTouched: currentMeta?.isTouched ?? false,
                isBlurred: currentMeta?.isBlurred ?? false,
                isDirty: currentMeta?.isDirty ?? false,
                isValidating: currentMeta?.isValidating ?? false,
                isPristine: currentMeta?.isPristine ?? true,
                errors: [errorMessage],
                errorMap: {
                  onChange: errorMessage,
                },
              });
            }

            fieldErrorMessages.push(`${field.customFieldDefinition.name}: ${errorMessage}`);

            logger.error("Failed to update custom field", {
              definitionId,
              fieldName: field.customFieldDefinition.name,
              error: fieldError,
              fieldInstance: fieldInstance,
            });

            // Don't throw, continue with other fields
          }
        }

        // De geboortedatum loopt via het geverifieerde endpoint en pas nadat je hem bevestigd
        // hebt: hij bepaalt mede je loonschaal. Het venster ging eerst open en daarna kwam de
        // toast "opgeslagen" -- terwijl er van die datum nog niets opgeslagen wás, en een tik op
        // Annuleren daar niets aan veranderde. Nu is de volgorde omgekeerd: wat zeker is meldt
        // zich, en de datum meldt zich pas als hij door is.
        const needsDayOfBirthConfirmation = hasDayOfBirthChanged(
          me?.employee?.dayOfBirth,
          value.dayOfBirth,
        );

        if (hasFieldErrors) {
          toast.error(t("fields:custom.save-failed"), { id: TOAST_KEY });
        } else if (!needsDayOfBirthConfirmation) {
          toast.success(t("actions:update-me.success"), { id: TOAST_KEY });
        }

        if (needsDayOfBirthConfirmation) {
          pendingDobRef.current = toDateInputValue(value.dayOfBirth);
          dobConfirmRef.current?.present();
        }
      } catch (error: any) {
        // Check for unique constraint errors
        let errorMessage = t("actions:update-me.error");

        // ResponseError structure: { statusCode, message }
        // message can be a string or an array of strings
        const statusCode = error?.status || error?.statusCode || error?.cause?.statusCode;
        const messageRaw = error?.cause?.message || error?.message || "";

        // Convert message to string (it might be an array)
        const message = Array.isArray(messageRaw) ? messageRaw.join(" ") : messageRaw;

        if (statusCode === 409) {
          // Check if it's a phone unique constraint error
          if (message.includes("Unique constraint failed") && message.includes("`phone`")) {
            errorMessage = t("actions:update-me.error-phone-exists");
          }
          // Check if it's an email unique constraint error
          else if (message.includes("Unique constraint failed") && message.includes("`email`")) {
            errorMessage = t("actions:update-me.error-email-exists");
          }
        }

        toast.error(errorMessage, { id: TOAST_KEY });
        logger.error("Failed to update personal info", error);
      }
    },
  });

  const renderCustomField = (field: CustomField) => {
    const { customFieldDefinition } = field;
    const { id, name, type, required, options } = customFieldDefinition;

    // #10a: a field flagged not editableByEmployee stays visible but read-only.
    const readOnly = !isEmployeeEditableCustomField(field);

    switch (type) {
      case "TEXT":
        return (
          <form.Field
            key={id}
            name={id}
            children={(field: any) => (
              <CustomFieldInput
                field={field}
                label={name}
                required={required}
                readOnly={readOnly}
                className={FIELD_IN_ROW}
              />
            )}
          />
        );

      case "NUMBER":
        return (
          <form.Field
            key={id}
            name={id}
            children={(field: any) => (
              <CustomFieldInput
                field={field}
                label={name}
                type="number"
                required={required}
                readOnly={readOnly}
                className={FIELD_IN_ROW}
              />
            )}
          />
        );

      case "DATE":
        return (
          <form.Field
            key={id}
            name={id}
            children={(field: any) => (
              <CustomFieldInput
                field={field}
                label={name}
                type="date"
                required={required}
                readOnly={readOnly}
                className={FIELD_IN_ROW}
              />
            )}
          />
        );

      case "SELECT":
        return (
          <form.Field
            key={id}
            name={id}
            children={(field: any) => (
              <CustomFieldSelect
                field={field}
                label={name}
                options={options || []}
                required={required}
                readOnly={readOnly}
              />
            )}
          />
        );

      case "MULTI_SELECT":
        return (
          <form.Field
            key={id}
            name={id}
            children={(field: any) => (
              <CustomFieldMultiSelect
                field={field}
                label={name}
                options={options || []}
                required={required}
                readOnly={readOnly}
              />
            )}
          />
        );

      case "BOOLEAN":
        return (
          <form.Field
            key={id}
            name={id}
            children={(field: any) => (
              <CustomFieldToggle field={field} label={name} readOnly={readOnly} />
            )}
          />
        );

      case "FILE":
        return (
          <form.Field
            key={id}
            name={id}
            children={(field: any) => (
              <CustomFieldFile
                field={field}
                label={name}
                required={required}
                readOnly={readOnly}
                userId={me?.id}
                definitionId={id}
              />
            )}
          />
        );

      default:
        return null;
    }
  };

  return (
    <FormElement>
      <IonPage>
        <IonHeader>
          <Toolbar>
            <Buttons slot="start">
              <BackButton defaultHref={Routes.Settings()} />
            </Buttons>
            <IonTitle>{t("routes:personal-information.title")}</IonTitle>
          </Toolbar>
        </IonHeader>
        <Content scrollUnderChrome>
          <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
            <IonRefresherContent refreshingSpinner="circles" />
          </IonRefresher>

          {/* Geen eigen inspring: de scroller heeft er al zestien pixels, en dat is precies de
              marge waarop de kaarten van een instellingenscherm horen te staan. Twee lagen padding
              was wat Diensten eerder te smal maakte. */}
          <div className="flex flex-col gap-8 py-6">
            <ProfileSection
              id="photo"
              title={t("routes:personal-information.sections.photo")}
              missing={missesPhoto}
              missingLabel={missingLabel}
            >
              <div className="flex flex-col items-center gap-3 px-4 py-6">
                <EditableAvatar userId={me?.id ?? ""} imageKey={me?.image ?? ""} />
                <p className="text-13 text-txt-tertiary">
                  {t("routes:personal-information.sections.photo-hint")}
                </p>
              </div>
            </ProfileSection>

            <ProfileSection
              id="name"
              title={t("routes:personal-information.sections.name")}
              missing={missesDayOfBirth}
              missingLabel={missingLabel}
            >
              <Row>
                <form.Field
                  name="firstname"
                  validators={requiredField("fields:firstname.error.required")}
                  children={(field: any) => (
                    <CustomFieldInput
                      field={field}
                      label={t("fields:firstname.label")}
                      required
                      className={FIELD_IN_ROW}
                    />
                  )}
                />
              </Row>

              <Row>
                <form.Field
                  name="lastname"
                  validators={requiredField("fields:lastname.error.required")}
                  children={(field: any) => (
                    <CustomFieldInput
                      field={field}
                      label={t("fields:lastname.label")}
                      required
                      className={FIELD_IN_ROW}
                    />
                  )}
                />
              </Row>

              <Row>
                <form.Field
                  name="dayOfBirth"
                  children={(field: any) => (
                    <CustomFieldInput
                      field={field}
                      label={t("fields:dayOfBirth.label", "Geboortedatum")}
                      type="date"
                      className={FIELD_IN_ROW}
                    />
                  )}
                />
              </Row>
            </ProfileSection>

            <ProfileSection
              id="contact"
              title={t("routes:personal-information.sections.contact")}
              missingLabel={missingLabel}
            >
              <Row>
                <form.Field
                  name="email"
                  validators={requiredField("fields:email.error.required")}
                  children={(field: any) => (
                    <CustomFieldInput
                      field={field}
                      label={t("fields:email.label")}
                      type="email"
                      required
                      className={FIELD_IN_ROW}
                    />
                  )}
                />
              </Row>

              <Row>
                <form.Field
                  name="phone"
                  validators={requiredField("fields:phone.error.required")}
                  children={(field: any) => (
                    <CustomFieldInput
                      field={field}
                      label={t("fields:phone.label")}
                      type="tel"
                      required
                      className={FIELD_IN_ROW}
                    />
                  )}
                />
              </Row>
            </ProfileSection>

            <ProfileSection
              id="address"
              title={t("routes:personal-information.sections.address")}
              missing={missesAddress}
              missingLabel={missingLabel}
            >
              <Row>
                <form.Field
                  name="addressLine"
                  validators={requiredField("fields:addressLine.error.required")}
                  children={(field: any) => (
                    <CustomFieldInput
                      field={field}
                      label={t("fields:addressLine.label")}
                      required
                      className={FIELD_IN_ROW}
                    />
                  )}
                />
              </Row>

              <Row>
                <form.Field
                  name="zipCode"
                  validators={requiredField("fields:zipCode.error.required")}
                  children={(field: any) => (
                    <CustomFieldInput
                      field={field}
                      label={t("fields:zipCode.label")}
                      required
                      className={FIELD_IN_ROW}
                    />
                  )}
                />
              </Row>

              <Row>
                <form.Field
                  name="city"
                  validators={requiredField("fields:hometown.error.required")}
                  children={(field: any) => (
                    <CustomFieldInput
                      field={field}
                      label={t("fields:hometown.label")}
                      required
                      className={FIELD_IN_ROW}
                    />
                  )}
                />
              </Row>

              {/* Provincie is niet langer verplicht. Hij stond dat wel, en blokkeerde daarmee het
                  opslaan van al het andere -- terwijl de server hem niet eens meetelt: de
                  volledigheidscheck kijkt naar adres, postcode en woonplaats, en niet hiernaar. */}
              <Row>
                <form.Field
                  name="state"
                  children={(field: any) => (
                    <CustomFieldInput
                      field={field}
                      label={t("fields:state.label")}
                      className={FIELD_IN_ROW}
                    />
                  )}
                />
              </Row>
            </ProfileSection>

            {customFields && customFields.length > 0 && (
              <ProfileSection
                id="custom"
                title={t("routes:personal-information.sections.custom")}
                missing={missesCustomField}
                missingLabel={missingLabel}
              >
                {customFields.map((field) => (
                  <Row key={field.customFieldDefinition.id}>{renderCustomField(field)}</Row>
                ))}
              </ProfileSection>
            )}

            <ProfileTeamsSection teams={me?.employee?.teams} />
          </div>
        </Content>

        {/* Day-of-birth verification confirmation (#383) */}
        <ModalSheetAutoHeight
          ref={dobConfirmRef}
          onWillDismiss={(event) => {
            if (event.detail.role === "confirm") {
              void saveVerifiedDayOfBirth();
            } else {
              // De rest van het formulier is op dit moment al opgeslagen; alleen de datum niet.
              // Zwijgen zou lezen als "er is niets gebeurd".
              pendingDobRef.current = null;
              toast.success(t("actions:update-me.success-without-day-of-birth"), { id: TOAST_KEY });
            }
          }}
        >
          <div className="ion-padding flex flex-col gap-6 pt-6">
            <strong className="text-lg text-txt-primary">
              {t("modals:day-of-birth-verify.title", "Geboortedatum bevestigen")}
            </strong>
            <p className="text-sm text-txt-secondary">
              {t(
                "modals:day-of-birth-verify.description",
                "Je geboortedatum bepaalt mede je loonschaal. Controleer of de datum klopt voordat je opslaat.",
              )}
            </p>
            <strong className="text-base text-txt-primary" data-testid="dob-confirm-value">
              {pendingDobRef.current}
            </strong>
            <div className="flex flex-col gap-2">
              <Button onClick={() => dobConfirmRef.current?.dismiss(undefined, "confirm")}>
                {t("modals:day-of-birth-verify.confirm", "Ja, klopt")}
              </Button>
              <Button
                color="primary"
                fill="outline"
                onClick={() => dobConfirmRef.current?.dismiss()}
              >
                {t("common:cancel", "Annuleren")}
              </Button>
            </div>
          </div>
        </ModalSheetAutoHeight>
        <IonFooter>
          <Toolbar>
            <button
              type="button"
              onClick={() => form.handleSubmit()}
              disabled={form.state.isSubmitting}
              className="w-full rounded-lg bg-button-primary py-3 text-center text-base font-semibold text-txt-inverted transition-opacity disabled:opacity-50"
            >
              {form.state.isSubmitting ? t("actions:update-me.loading") : t("common:save")}
            </button>
          </Toolbar>
        </IonFooter>
      </IonPage>
    </FormElement>
  );
}
